import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { clsx } from '../../../../ds/clsx';
import { useIsMobile } from '../../../../ds/hooks';
import styles from './OrgSettingsPage.module.css';

export interface OrgSettingsPageProps {
  title: string;
  description: ReactNode;
  /** Right end of the header row (E22 status pill). */
  aside?: ReactNode | undefined;
  /** The own tab row „Marke · Rechtstexte" (E20/E21 share one navigation entry). */
  tabs?: boolean | undefined;
  /** Row gap of the page: 22 (E21, E22), 24 (E20), 20 (E24). */
  gap?: 20 | 22 | 24 | undefined;
  children: ReactNode;
}

/**
 * Page chrome of Einstellungen 20–24 inside the settings frame. Organization settings are „nur am Rechner"
 * (Einstellungen M1): below 768 px the page shows that note instead of its content.
 */
export function OrgSettingsPage({
  title,
  description,
  aside,
  tabs = false,
  gap = 22,
  children,
}: OrgSettingsPageProps) {
  const mobile = useIsMobile();
  if (mobile) {
    return (
      <div className={styles.desktopOnly}>
        <span className={styles.desktopOnlyTitle}>{title}</span>
        <span className={styles.desktopOnlyText}>Nur am Rechner bearbeitbar</span>
      </div>
    );
  }
  return (
    <div className={clsx(styles.page, gap === 20 && styles.gap20, gap === 24 && styles.gap24)}>
      {tabs ? (
        <nav className={styles.tabs} aria-label="Marke und Rechtstexte">
          <NavLink
            to="/settings/organization/brand"
            className={({ isActive }) => clsx(styles.tab, isActive && styles.tabActive)}
          >
            Marke
          </NavLink>
          <NavLink
            to="/settings/organization/legal-texts"
            className={({ isActive }) => clsx(styles.tab, isActive && styles.tabActive)}
          >
            Rechtstexte
          </NavLink>
        </nav>
      ) : null}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Organisation</span>
            <span className={styles.adminTag}>Admin</span>
          </span>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
        </div>
        {aside ? <div className={styles.aside}>{aside}</div> : null}
      </div>
      {children}
    </div>
  );
}
